import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import GalleryPage from "@/pages/GalleryPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import AboutPage from "@/pages/AboutPage";
import ReviewsPage from "@/pages/ReviewsPage";
import QuotePage from "@/pages/QuotePage";
import BookingPage from "@/pages/BookingPage";
import ContactPage from "@/pages/ContactPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import BathroomRefitPage from "@/pages/BathroomRefitPage";
import FloorTilingPage from "@/pages/FloorTilingPage";
import FinancePage from "@/pages/FinancePage";
import EstimateMenuPage from "@/pages/EstimateMenuPage";
import TileCalculatorPage from "@/pages/TileCalculatorPage";

// Owner Control Panel
import OwnerLoginPage from "@/pages/OwnerLoginPage";
import OwnerSetupPage from "@/pages/OwnerSetupPage";
import OwnerDashboard from "@/pages/OwnerDashboard";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";

// Customer Portal
import CustomerLoginPage from "@/pages/CustomerLoginPage";
import CustomerPortal from "@/pages/CustomerPortal";

// SEO Location Pages
import SEOPage from "@/pages/SEOPage";
import BathroomRefitSEOPage from "@/pages/BathroomRefitSEOPage";
import FinanceSEOPage from "@/pages/FinanceSEOPage";

// Layout
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import OwnerLayout from "@/components/OwnerLayout";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/estimate" element={<EstimateMenuPage />} />
            <Route path="/tile-calculator" element={<TileCalculatorPage />} />
            <Route path="/bathroom-refit" element={<BathroomRefitPage />} />
            <Route path="/bathroom-refit-quote" element={<BathroomRefitPage />} />
            <Route path="/floor-tiling-quote" element={<FloorTilingPage />} />
            <Route path="/finance" element={<FinancePage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* SEO Location Pages - Greater Manchester */}
            <Route path="/bathroom-tiler-manchester" element={<SEOPage location="Manchester" service="Bathroom Tiling" />} />
            <Route path="/kitchen-tiling-manchester" element={<SEOPage location="Manchester" service="Kitchen Tiling" />} />
            <Route path="/floor-tiling-manchester" element={<SEOPage location="Manchester" service="Floor Tiling" />} />
            <Route path="/bathroom-tiler-salford" element={<SEOPage location="Salford" service="Bathroom Tiling" />} />
            <Route path="/kitchen-tiling-salford" element={<SEOPage location="Salford" service="Kitchen Tiling" />} />
            <Route path="/bathroom-tiler-stockport" element={<SEOPage location="Stockport" service="Bathroom Tiling" />} />
            <Route path="/kitchen-tiling-stockport" element={<SEOPage location="Stockport" service="Kitchen Tiling" />} />
            <Route path="/bathroom-tiler-trafford" element={<SEOPage location="Trafford" service="Bathroom Tiling" />} />
            <Route path="/bathroom-tiler-bolton" element={<SEOPage location="Bolton" service="Bathroom Tiling" />} />
            <Route path="/bathroom-tiler-bury" element={<SEOPage location="Bury" service="Bathroom Tiling" />} />
            
            {/* Bathroom Refit SEO Pages */}
            <Route path="/bathroom-renovation-manchester" element={<BathroomRefitSEOPage location="Manchester" />} />
            <Route path="/bathroom-refit-manchester" element={<BathroomRefitSEOPage location="Manchester" />} />
            <Route path="/bathroom-installation-manchester" element={<BathroomRefitSEOPage location="Manchester" />} />
            
            {/* Finance SEO Pages */}
            <Route path="/bathroom-finance-manchester" element={<FinanceSEOPage location="Manchester" />} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="quotes" element={<AdminDashboard view="quotes" />} />
            <Route path="bookings" element={<AdminDashboard view="bookings" />} />
            <Route path="contacts" element={<AdminDashboard view="contacts" />} />
            <Route path="projects" element={<AdminDashboard view="projects" />} />
            <Route path="reviews" element={<AdminDashboard view="reviews" />} />
          </Route>

          {/* Owner Control Panel Routes */}
          <Route path="/owner-login" element={<OwnerLoginPage />} />
          <Route path="/owner-setup" element={<OwnerSetupPage />} />
          <Route path="/owner-dashboard" element={<OwnerLayout />}>
            <Route index element={<OwnerDashboard />} />
            <Route path="projects" element={<OwnerDashboard view="projects" />} />
            <Route path="leads" element={<OwnerDashboard view="leads" />} />
            <Route path="pipeline" element={<OwnerDashboard view="pipeline" />} />
            <Route path="portfolio" element={<OwnerDashboard view="portfolio" />} />
            <Route path="reviews" element={<OwnerDashboard view="reviews" />} />
            <Route path="pricing" element={<OwnerDashboard view="pricing" />} />
            <Route path="calendar" element={<OwnerDashboard view="calendar" />} />
            <Route path="payments" element={<OwnerDashboard view="payments" />} />
            <Route path="notifications" element={<OwnerDashboard view="notifications" />} />
            <Route path="integrations" element={<OwnerDashboard view="integrations" />} />
            <Route path="test" element={<OwnerDashboard view="test" />} />
            <Route path="taptrack" element={<OwnerDashboard view="taptrack" />} />
            <Route path="settings" element={<OwnerDashboard view="settings" />} />
            <Route path="operations" element={<OwnerDashboard view="operations" />} />
            <Route path="analytics" element={<OwnerDashboard view="analytics" />} />
            <Route path="reminders" element={<OwnerDashboard view="reminders" />} />
            <Route path="referrals" element={<OwnerDashboard view="referrals" />} />
            <Route path="bank-settings" element={<OwnerDashboard view="bank-settings" />} />
            <Route path="email-settings" element={<OwnerDashboard view="email-settings" />} />
          </Route>
          
          {/* Payment Success Page */}
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          
          {/* Customer Portal Routes */}
          <Route path="/customer-login" element={<CustomerLoginPage />} />
          <Route path="/customer-portal" element={<CustomerPortal />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
